import {HttpClient, HttpParams} from '@angular/common/http';
import {EMPTY, expand, map, Observable, reduce} from 'rxjs';
import {GenericResponseWithPagination} from '../_types/shared';
import {takeUntil} from 'rxjs/operators';

export function fetchAllPages<T>(http: HttpClient, url: string, pageSize: number = 1000, params?: HttpParams): Observable<T[]> {
    params = (params ?? new HttpParams()).append('page_size', pageSize.toString());
    return http.get<GenericResponseWithPagination<T>>(url, {params}).pipe(
        expand(response => !!response?.next ? http.get<GenericResponseWithPagination<T>>(response.next) : EMPTY),
        map(response => response ? response.results : []),
        reduce((acc, results) => acc.concat(results), [] as T[])
    );
}

const dynamicIntervals: number[] = [1000, 5000, 10000, 30000, 60000]; // Array of delays in milliseconds

export function createPeriodicCalls<T>(observable: Observable<T>, stop$: Observable<any>): Observable<T> {
    return new Observable<T>((observer): void => {
        let index: number = 0;

        const callNext: () => void = (): void => {
            const delay: number = dynamicIntervals[index] || dynamicIntervals[dynamicIntervals.length - 1];
            observable
                .pipe(map((res) => {
                    return isCSVResponse(res) ? ({data: {status: 'SUCCESS'}} as any) : JSON.parse(res as string);
                }))
                .subscribe({
                    next: (result: any): void => {
                        observer.next(result);
                        if (result?.data?.status === 'SUCCESS') {
                            observer.complete();
                            return;
                        }
                        index++;
                        if (index < dynamicIntervals.length) {
                            setTimeout(callNext, delay);
                        } else {
                            index = dynamicIntervals.length - 1;
                            setTimeout(callNext, delay);
                        }
                    },
                    error: (err): void => {
                        observer.error(err);
                    },
                });
        };

        // Стартувати виклики
        callNext();
    }).pipe(takeUntil(stop$));
}

function isCSVResponse(response: any): boolean {
    if (response && response.headers) {
        const contentType = response.headers.get('Content-Type');
        return contentType && contentType.includes('text/csv');
    }
    if (typeof response === 'string') {
        // Check if it includes line breaks (which CSV often has) and commas (as basic CSV separators)
        const hasLineBreak: boolean = response.includes('\n');
        const hasComma: boolean = response.includes(',');

        // A simple heuristic for CSV (you can refine this check based on your specific CSV structure)
        if (hasLineBreak && hasComma) {
            return true;
        }
    }
    return false;
}
